import { useEffect, useRef, useState } from "react";
import { clearDirty, isDirty, type DirtyViewScope } from "../lib/dirtyViews";
import { queryClient } from "../lib/queryClient";

interface RefetchResultShape {
  error?: unknown;
  isError?: boolean;
  status?: string;
}

function didRefetchSucceed(result: unknown) {
  const shaped = result as RefetchResultShape | undefined;
  if (!shaped) return true;
  if (shaped.error) return false;
  if (shaped.isError === true) return false;
  if (shaped.status === "error") return false;
  return true;
}

export function useLazyDirtyRefreshOnFocus(
  scope: DirtyViewScope,
  poolCode: string | null,
  isFocused: boolean,
  refetch: () => Promise<unknown>,
) {
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => {
    if (typeof document === "undefined") {
      return true;
    }
    return document.visibilityState === "visible";
  });
  const wasFocusedRef = useRef(isFocused);
  const wasDocumentVisibleRef = useRef(isDocumentVisible);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const handleVisibility = () => {
      setIsDocumentVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    window.addEventListener("blur", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
      window.removeEventListener("blur", handleVisibility);
    };
  }, []);

  useEffect(() => {
    const wasFocused = wasFocusedRef.current;
    const wasDocumentVisible = wasDocumentVisibleRef.current;
    wasFocusedRef.current = isFocused;
    wasDocumentVisibleRef.current = isDocumentVisible;

    const becameFocused = isFocused && !wasFocused;
    const becameVisible = isDocumentVisible && !wasDocumentVisible;
    const canAttemptRefresh = becameFocused || becameVisible;

    if (!poolCode) return;
    if (!isFocused) return;
    if (!isDocumentVisible) return;
    if (!canAttemptRefresh) return;
    if (!isDirty(queryClient, scope, poolCode)) return;
    if (inFlightRef.current) return;

    inFlightRef.current = true;
    void (async () => {
      try {
        const result = await refetch();
        if (didRefetchSucceed(result)) {
          clearDirty(queryClient, scope, poolCode);
        }
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [isDocumentVisible, isFocused, poolCode, refetch, scope]);
}
